import type { CSSProperties } from 'react';
import React, { useState } from 'react';
import ReactContentEditable from 'react-contenteditable';
import { CurrentSelectionStrategy } from '../steps/CardDesign/SelectionStrategy';

interface ContentEditableProps {
  value: string;
  isEditing: boolean;
  onChange?: (value: string) => void;
  style?: CSSProperties;
}

// `value` snapshot on init. This suspends 2-way binding.
// If we set ContentEditable.html to something other than what was previously typed,
// the browser will reset the caret position. Thus we want to prevent that loop.
const Root = (props: ContentEditableProps) => {
  const { value, isEditing, onChange, style } = props;
  const ref = CurrentSelectionStrategy.useInitTextCaret(isEditing);
  const [html, setHtml] = useState(`${value}\n`);

  return (
    <div style={style}>
      <ReactContentEditable
        innerRef={ref}
        html={html}
        onChange={e => {
          setHtml(e.target.value);
          const innertText = ref.current?.innerText ?? '';
          onChange?.(innertText.replace(/\n$/, ''));
        }}
        style={
          {
            outline: 'none',
            display: 'inline-block',
          } as CSSProperties
        }
      />
    </div>
  );
};

// Issue: The caret will intermittently jump to the end of the text when typing.
// Likely related to: https://github.com/lovasoa/react-contenteditable/issues/161
// Seems to repro when we have:
//   1) Text with "&". The way it encodes into an entity likely interferes with state comparison and therefore updates.
//   2) Frequent re-renders. In our case caused by mouse move events elsewhere in the react tree.
// The React.memo removes the render noise from #2.
// We can replace with a more precise solution when have time to look at `react-contenteditable` deeper.
export const ContentEditable = React.memo(Root, (prevProps, nextProps) => {
  // prettier-ignore
  return prevProps.value === nextProps.value
    && prevProps.isEditing === nextProps.isEditing
    // Not comparing `___props.onChange` as it is unstable.
    && JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style);
});
